import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";

import CHBAResultsFilters from "./Filters";
import Select from "components/Input/Select";
import ProgressBar from "components/ProgressBar";
import BarGraph from "components/BarChart";
import Loading from "components/Loading";
import { Table, TableRow, TablePagination, TableFooter } from "@mui/material";

import { fetchCommFilterOptions, getCommModelResults } from "utils/communities";
import { getSortableName } from "utils/directory/sorting";

import { selectCommunityId } from "store/community/selectors";
import { fetchCommModelDetails } from "store/community/thunk";

import classes from "../style.module.scss";

const CHBAResults = ({
    communityId,
    commModelDir: { models },
    fetchCommModelDetails,
    defaultRowsPerPage = 5,
    rowsPerPageOptions = [5, 10, 25, 50],
}) => {
    const [loading, setLoading] = useState(true);

    // Store all model results, modelIds with no results, and filtered results
    const [allModels, setAllModels] = useState([]);
    const [noResults, setNoResults] = useState([]);
    const [filteredModels, setFilteredModels] = useState([]);

    // Percentage of models with valid results to display
    const [progress, setProgress] = useState(0);

    // Selected metric for graphs: energy, opEmissions, opCost, renoCost
    const [graphMetric, setGraphMetric] = useState("energy");

    // Options for filters
    const [locationOpts, setLocationOpts] = useState([]);
    const [userOpts, setUserOpts] = useState([]);
    const [modelOpts, setModelOpts] = useState([]);

    // Fetch and store results for each model on first render of page
    useEffect(() => {
        if (!communityId) {
            return;
        }

        const modelIds = Object.keys(models);

        let allResults = [];
        let emptyResults = [];
        let resultsPromise = null;

        for (let i = 0; i < modelIds.length; i++) {
            let modelId = modelIds[i];
            resultsPromise = fetchCommModelDetails(communityId, modelId).then((data) => {
                let results = getCommModelResults(models, modelId, data);
                if (results) allResults.push(results);
                else {
                    emptyResults.push(models[modelId]);
                }
                return allResults;
            });
        }

        // Gets final results list and initialize model lists and options
        resultsPromise.then((allResults) => {
            // Sort alphabetically
            allResults.sort((a, b) => getSortableName(a?.name).localeCompare(getSortableName(b?.name)));
            emptyResults.sort((a, b) => getSortableName(a?.name).localeCompare(getSortableName(b?.name)));

            // Store all model results, and initialize filtered model results
            setAllModels(allResults);
            setFilteredModels(allResults);
            setNoResults(emptyResults);
            setProgress((allResults.length / modelIds.length) * 100);

            // Set filter dropdown options
            const { locationOpts, userOpts, modelOpts } = fetchCommFilterOptions(allResults);
            setLocationOpts(locationOpts);
            setUserOpts(userOpts);
            setModelOpts(modelOpts);

            // Stop loading
            setLoading(false);
        });
    }, [communityId]);

    /* PAGINATION */

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Graphs to display on the current page
    const paginatedRows =
        rowsPerPage > 0 ? filteredModels.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : filteredModels;

    // The maximum values for each metric among the current page's results
    const maxValues = {
        energy: Math.max(...paginatedRows.map((model) => model.maxValues.energy)),
        opEmissions: Math.max(...paginatedRows.map((model) => model.maxValues.opEmissions)),
        opCost: Math.max(...paginatedRows.map((model) => model.maxValues.opCost)),
        renoCost: Math.max(...filteredModels.map((model) => model.renoCost)),
    };

    return (
        <div className={classes.contentContainer}>
            <div className={classes.header}>
                <CHBAResultsFilters
                    allModels={allModels}
                    setFilteredModels={setFilteredModels}
                    graphMetric={graphMetric}
                    locationOpts={locationOpts}
                    userOpts={userOpts}
                    modelOpts={modelOpts}
                    setPage={setPage}
                />

                <ProgressBar
                    loading={loading}
                    progress={progress}
                    breakdown={noResults}
                    className={classes.progressBar}
                />
            </div>

            <Select
                hideLabel
                disabled={loading}
                className={classes.graphTitle}
                name={"metricShown"}
                label={"Metric"}
                options={[
                    {
                        label: "Energy Consumption (GJ)",
                        value: "energy",
                    },
                    {
                        label: "Operational Emissions (t/yr)",
                        value: "opEmissions",
                    },
                    {
                        label: "Operational Cost ($/yr)",
                        value: "opCost",
                    },
                    {
                        label: "Renovation Cost ($)",
                        value: "renoCost",
                    },
                ]}
                setValue={graphMetric}
                input={{
                    value: graphMetric,
                    onChange: (value) => {
                        setGraphMetric(value);
                        // handleApply(value);
                    },
                }}
            />

            {loading && <Loading className={`${classes.spinner} ${classes.loadingResults}`} />}
            {!loading && filteredModels.length === 0 && (
                <p className={classes.noResultsText}> There are no results available for the models selected. </p>
            )}
            {!loading &&
                filteredModels.length > 0 &&
                (graphMetric === "renoCost" ? (
                    <BarGraph allModeldata={filteredModels} metric={graphMetric} maxValues={maxValues} />
                ) : (
                    paginatedRows.map((model) => (
                        <BarGraph key={model.key} singleModelData={model} metric={graphMetric} maxValues={maxValues} />
                    ))
                ))}
            {graphMetric !== "renoCost" && filteredModels.length > rowsPerPage && (
                <Table>
                    <TableFooter style={{ backgroundColor: "white" }}>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={rowsPerPageOptions}
                                count={filteredModels.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                SelectProps={{
                                    inputProps: {
                                        "aria-label": "rows per page",
                                    },
                                }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            )}
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    communityId: selectCommunityId,
});

const mapDispatchToProps = (dispatch) => ({
    fetchCommModelDetails: async (communityId, modelId) => {
        return dispatch(fetchCommModelDetails(communityId, modelId));
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(CHBAResults);
